import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, MessagesSquare, Map, Compass, UserRound, LayoutGrid, Sparkles, Star, Library } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { PremiumDialog } from "@/components/PremiumDialog";

const nav = [
  { to: "/dashboard", label: "Command", icon: LayoutGrid, id: "nav-dashboard" },
  { to: "/interview", label: "Interview", icon: MessagesSquare, id: "nav-interview" },
  { to: "/profile", label: "Profile", icon: UserRound, id: "nav-profile" },
  { to: "/paths", label: "Paths", icon: Compass, id: "nav-paths" },
  { to: "/roadmap", label: "Roadmap", icon: Map, id: "nav-roadmap" },
];

export const Shell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [premium, setPremium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get("/premium/status").then((s) => setPremium(s.premium)).catch(() => {});
    api.get("/admin/status").then((s) => setIsAdmin(s.is_admin)).catch(() => {});
  }, [premiumOpen]);

  return (
    <div className="min-h-screen grid-lines">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center gap-8">
          <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0" data-testid="brand-link">
            <span className="h-2 w-2 rounded-full bg-primary shadow-[0_0_14px_hsl(187_72%_58%)]" />
            <span className="font-display text-lg tracking-tight">loop</span>
            {premium && (
              <span className="ml-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-accent/40 text-accent" data-testid="pro-badge">
                pro
              </span>
            )}
          </Link>
          <nav className="flex items-center gap-1 overflow-x-auto">
            {nav.map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={id}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors duration-200 whitespace-nowrap ${
                    isActive
                      ? "bg-white/[0.07] text-white"
                      : "text-muted-foreground hover:bg-white/[0.04] hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.6} />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto relative">
            <button
              onClick={() => setMenu((m) => !m)}
              data-testid="account-menu-button"
              aria-label="Account menu"
              className="flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-full hover:bg-white/[0.06] transition-colors duration-200"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/10" data-testid="user-avatar" />
              ) : (
                <span className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center text-xs" data-testid="user-avatar">
                  {user?.name?.[0]}
                </span>
              )}
              <span className="hidden sm:block text-sm text-muted-foreground" data-testid="user-name">
                {user?.name}
              </span>
            </button>

            {menu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} aria-hidden="true" />
                <div className="absolute right-0 mt-2 w-64 glass z-50 p-2 rise" data-testid="account-menu">
                  <button
                    onClick={() => {
                      setMenu(false);
                      track("upgrade_clicked", { placement: "account_menu", is_pro: premium });
                      setPremiumOpen(true);
                    }}
                    data-testid="open-premium-button"
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm rounded-sm hover:bg-white/[0.07] hover:text-white transition-colors duration-200"
                  >
                    <Sparkles className="h-4 w-4 text-accent" strokeWidth={1.7} />
                    {premium ? (
                      "Manage Loop Pro"
                    ) : (
                      <span>
                        Get Loop Pro
                        <span className="block text-xs text-muted-foreground mt-0.5">$9.99/mo or $79/yr</span>
                      </span>
                    )}
                  </button>
                  {isAdmin && (
                    <Link
                      to="/admin/resources"
                      onClick={() => setMenu(false)}
                      data-testid="admin-resources-link"
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm rounded-sm text-muted-foreground hover:bg-white/[0.07] hover:text-white transition-colors duration-200"
                    >
                      <Library className="h-4 w-4" strokeWidth={1.7} />
                      <span>
                        Resource library
                        <span className="block text-xs text-muted-foreground mt-0.5">Affiliate admin</span>
                      </span>
                    </Link>
                  )}
                  <a
                    href={`mailto:loop.int.review@gmail.com?subject=${encodeURIComponent(
                      "Loop review"
                    )}&body=${encodeURIComponent(
                      "How many stars out of 5?\n\nWhat worked well for you?\n\nWhat should Loop do better?\n\n"
                    )}`}
                    onClick={() => setMenu(false)}
                    data-testid="leave-review-link"
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm rounded-sm text-muted-foreground hover:bg-white/[0.07] hover:text-white transition-colors duration-200"
                  >
                    <Star className="h-4 w-4" strokeWidth={1.7} />
                    <span>
                      Leave a review
                      <span className="block text-xs text-muted-foreground mt-0.5">Opens your email app</span>
                    </span>
                  </a>
                  <button
                    onClick={() => logout().catch(() => navigate("/"))}
                    data-testid="logout-button"
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm rounded-sm text-muted-foreground hover:bg-white/[0.07] hover:text-white transition-colors duration-200"
                  >
                    <LogOut className="h-4 w-4" strokeWidth={1.7} />
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">{children}</main>
      <PremiumDialog open={premiumOpen} onClose={() => setPremiumOpen(false)} />
    </div>
  );
};
